// Minimal, privacy-respecting security logger. Avoid raw PII (emails, tokens).
// UIDs are treated as pseudonymous identifiers; we mask them by default.

type SafeRecord = Record<string, unknown>;

function maskUid(uid: string | null | undefined): string | null {
  if (!uid || typeof uid !== 'string') return null;
  const tail = uid.slice(-6);
  return `uid:***${tail}`;
}

function sanitize(input?: SafeRecord): SafeRecord {
  const redactedKeys = new Set(['email', 'secondary_email', 'token', 'password', 'apiKey', 'apikey', 'auth_token']);
  const out: SafeRecord = {};
  if (!input) return out;
  for (const [k, v] of Object.entries(input)) {
    if (redactedKeys.has(k)) {
      out[k] = '[redacted]';
      continue;
    }
    out[k] = v;
  }
  return out;
}

export function logSecurity(event: string, data?: SafeRecord): void {
  try {
    const payload = sanitize(data);
    // eslint-disable-next-line no-console
    console.info('sec.event', { event, ...payload });
    try { window.dispatchEvent(new CustomEvent('sec:log', { detail: { event, ...payload } })); } catch {}
  } catch {}
}

export function logAuthLifecycle(event: string, uid?: string | null, extra?: SafeRecord): void {
  logSecurity('auth.lifecycle', { event, uid: maskUid(uid || null), ...(extra || {}) });
}

export function logCrossUidAttempt(currentUid: string | null, targetUid: string | null, context?: string): void {
  logSecurity('cross_uid_attempt', { currentUid: maskUid(currentUid), targetUid: maskUid(targetUid), context });
}

export function logImmutableBlocked(uid: string | null, fields: Array<{ field: string; from: unknown; to: unknown }>, context?: string): void {
  logSecurity('immutable_change_blocked', { uid: maskUid(uid), fields, context });
}

export function logRuleDenied(uid: string | null, operation: 'get' | 'list' | 'create' | 'update' | 'delete' | 'unknown', pathKind: string, context?: string): void {
  logSecurity('rules_denied', { uid: maskUid(uid), operation, pathKind, context });
}


